/**
 * API service for tenant settings
 */
import { apiGet, apiPut } from '../api/axiosInstance';
import type { ReportingBaseCurrency } from '@/generated/api/models/reportingBaseCurrency';
import type { TenantSettings, TenantSettingsUpdate } from '../types/tenantSettings';

const BASE_URL = '/api/v1/admin/settings';
const USER_MANAGEMENT_SETTINGS_URL = '/api/v1/user-management/settings';
const TENANT_PUBLIC_URL = '/api/v1/tenant';

interface SingleResponse<T> {
  data: T;
}

export interface UserDomain {
  domain: string;
  user_count: number;
  already_assigned: boolean;
}

export type { UserManagementMobileAppLockPolicy as MobileAppLockPolicy } from '../../generated/config-enums';
import type { UserManagementMobileAppLockPolicy as MobileAppLockPolicy } from '../../generated/config-enums';

export interface UserManagementTenantSettings {
  id: string | null;
  tenant_id: string;
  allow_user_invitations: boolean;
  allow_domain_self_registration: boolean;
  notify_admins_on_signup: boolean;
  mobile_app_lock_policy: MobileAppLockPolicy;
  mobile_app_lock_idle_minutes: number;
  settings: Record<string, unknown>;
}

export interface UserManagementTenantSettingsUpdate {
  allow_user_invitations?: boolean;
  allow_domain_self_registration?: boolean;
  notify_admins_on_signup?: boolean;
  mobile_app_lock_policy?: MobileAppLockPolicy;
  mobile_app_lock_idle_minutes?: number;
}

export interface TenantLocaleSettings {
  default_language: 'en' | 'es' | 'he' | null;
  default_number_format: 'language_default' | 'comma_dot' | 'dot_comma' | null;
  default_date_format: 'language_default' | 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd' | null;
  default_time_format: '12h' | '24h' | null;
  default_currency: string | null;
  brand_color: string | null;
  country: string | null;
  default_paper_format: string | null;
  default_unit_length: string | null;
  default_unit_distance: string | null;
  default_unit_weight: string | null;
  default_unit_volume: string | null;
  default_unit_liquid_volume: string | null;
  default_unit_area: string | null;
  default_unit_temperature: string | null;
  sso_user_can_set_password: boolean;
}

/** Tenant persona flags (Forwarder TMS Wave 1.2). Readable by any authenticated user. */
export interface TenantPersona {
  sells_freight_services: boolean;
}

// The ISO currency a base-currency KPI aggregate is denominated in — the
// frontend anchor mirroring `FxService.resolve_reporting_base_currency` (active
// company base → flagged accounting base → tenant default → USD). Readable by
// any authenticated user; scoped by the `X-Active-Company-Id` header.
//
// The generated model IS this type (identical single `currency_code: string`),
// re-exported so consumers keep importing it from here. The prose is a comment
// rather than a JSDoc because a re-export carries none.
export type { ReportingBaseCurrency } from '@/generated/api/models/reportingBaseCurrency';

/**
 * Per-module company-scoping participation (multi-company WI-4): `{ moduleId:
 * enabled }` for every eligible module. Readable by any authenticated user.
 */
export type CompanyScopingMap = Record<string, boolean>;

export const tenantSettingsApi = {
  getSettings: async (): Promise<TenantSettings> => {
    const response = await apiGet<SingleResponse<TenantSettings>>(BASE_URL);
    return response.data;
  },

  updateSettings: async (data: TenantSettingsUpdate): Promise<TenantSettings> => {
    const response = await apiPut<SingleResponse<TenantSettings>>(BASE_URL, data);
    return response.data;
  },

  getUserDomains: async (): Promise<UserDomain[]> => {
    const response = await apiGet<SingleResponse<UserDomain[]>>(`${BASE_URL}/user-domains`);
    return response.data;
  },

  getUserManagementSettings: async (): Promise<UserManagementTenantSettings> => {
    const response = await apiGet<UserManagementTenantSettings>(`${USER_MANAGEMENT_SETTINGS_URL}/tenant`);
    return response;
  },

  updateUserManagementSettings: async (data: UserManagementTenantSettingsUpdate): Promise<UserManagementTenantSettings> => {
    const response = await apiPut<UserManagementTenantSettings>(`${USER_MANAGEMENT_SETTINGS_URL}/tenant`, data);
    return response;
  },

  getLocaleSettings: async (): Promise<TenantLocaleSettings> => {
    const response = await apiGet<SingleResponse<TenantLocaleSettings>>(`${TENANT_PUBLIC_URL}/locale-settings`);
    return response.data;
  },

  getPersona: async (): Promise<TenantPersona> => {
    const response = await apiGet<SingleResponse<TenantPersona>>(`${TENANT_PUBLIC_URL}/persona`);
    return response.data;
  },

  /** Reporting base currency for the active company scope (base-currency KPI anchor). */
  getReportingBaseCurrency: async (): Promise<ReportingBaseCurrency> =>
    apiGet<ReportingBaseCurrency>(`${TENANT_PUBLIC_URL}/reporting-base-currency`),

  /** Per-module company-scoping participation map (multi-company WI-4). */
  getCompanyScoping: async (): Promise<CompanyScopingMap> => {
    const response = await apiGet<SingleResponse<CompanyScopingMap>>(
      `${TENANT_PUBLIC_URL}/company-scoping`,
    );
    return response.data;
  },

  /** Enable/disable company scoping for an opt-in module (tenant admin only). */
  setCompanyScoping: async (moduleId: string, enabled: boolean): Promise<CompanyScopingMap> => {
    const response = await apiPut<SingleResponse<CompanyScopingMap>>(
      `${TENANT_PUBLIC_URL}/company-scoping/${moduleId}`,
      { enabled },
    );
    return response.data;
  },
};
